import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@bookdental/ui";
import { trpc } from "@/lib/trpc";
import { AJUDA_FINANCEIRO } from "@/lib/ajuda/dialogos/financeiro";
import { reais, textoParaCentavos } from "@/lib/dinheiro";
import { cn } from "@/lib/cn";
import { campo, rotulo } from "../estilos";

const MOTIVOS = ["Cliente fechou", "Não responde à cobrança", "Acordo de desconto", "Valor em disputa"];

type Alvo = { extratoId: string } | { clienteId: string; competencia: string };
type Props = { aberto: boolean; onFechar: () => void; saldoCents: number; alvo: Alvo; descricao: string };

/** Dar como perdido: abate o saldo, entra como despesa no mês de hoje e não mexe no caixa. */
export function PerdaDialog({ aberto, onFechar, saldoCents, alvo, descricao }: Props) {
  const utils = trpc.useUtils();
  const [valor, setValor] = useState(reais(saldoCents));
  const [motivo, setMotivo] = useState("");
  useEffect(() => {
    if (!aberto) return;
    setValor(reais(saldoCents));
    setMotivo("");
  }, [aberto, saldoCents]);
  const aoSalvar = {
    onSuccess: () => {
      toast.success("Valor dado como perdido. Para desfazer, estorne em Lançamentos.");
      utils.financeiro.invalidate();
      onFechar();
    },
    onError: (e: { message: string }) => toast.error(e.message),
  };
  const doMes = trpc.financeiro.perdas.doMes.useMutation(aoSalvar);
  const doExtrato = trpc.financeiro.perdas.doExtrato.useMutation(aoSalvar);
  const valorCents = textoParaCentavos(valor);
  const valido = valorCents > 0 && valorCents <= saldoCents && motivo.trim().length >= 3;
  const salvar = () => {
    const dados = { valorCents, motivo: motivo.trim() };
    if ("extratoId" in alvo) doExtrato.mutate({ extratoId: alvo.extratoId, ...dados });
    else doMes.mutate({ clienteId: alvo.clienteId, competencia: alvo.competencia, ...dados });
  };

  return (
    <Dialog open={aberto} onOpenChange={(v) => !v && onFechar()}>
      <DialogContent className="max-w-md border border-zinc-200 bg-white shadow-xl dark:border-border dark:bg-card">
        <DialogHeader>
          <DialogTitle ajuda={AJUDA_FINANCEIRO.perda}>Dar como perdido?</DialogTitle>
          <DialogDescription>
            {descricao}. O valor sai do saldo a receber e entra como despesa (Perdas com clientes) no mês de hoje. O caixa não muda.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <label className="block">
            <span className={rotulo}>Valor (saldo em aberto: {reais(saldoCents)})</span>
            <input value={valor} onChange={(e) => setValor(e.target.value)} inputMode="decimal" className={cn(campo, valorCents > saldoCents && "border-red-400")} />
          </label>
          <div>
            <span className={rotulo}>Motivo</span>
            <div className="mb-2 flex flex-wrap gap-1.5">
              {MOTIVOS.map((m) => (
                <button key={m} type="button" onClick={() => setMotivo(m)} aria-pressed={motivo === m} className={cn("rounded-full border px-2.5 py-1 text-xs", motivo === m ? "border-brand-600 bg-brand-50 text-brand-700 dark:border-primary dark:bg-primary/10 dark:text-primary" : "border-zinc-200 text-zinc-600 hover:bg-zinc-50 dark:border-border dark:text-muted-foreground dark:hover:bg-muted")}>
                  {m}
                </button>
              ))}
            </div>
            <textarea value={motivo} onChange={(e) => setMotivo(e.target.value)} rows={2} maxLength={300} placeholder="Por que esse valor não vai ser recebido" className={cn(campo, "h-auto py-2")} />
          </div>
        </div>
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onFechar} className="h-9 rounded-md px-4 text-sm text-zinc-600 hover:bg-zinc-100 dark:text-muted-foreground dark:hover:bg-muted">
            Cancelar
          </button>
          <button type="button" disabled={!valido || doMes.isPending || doExtrato.isPending} onClick={salvar} className="h-9 rounded-md bg-red-600 px-4 text-sm font-semibold text-white shadow-sm hover:bg-red-700 disabled:opacity-50">
            Dar como perdido
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/** O link discreto "Dar como perdido" ao lado do registrar recebimento. */
export function BotaoDarComoPerdido(props: Omit<Props, "aberto" | "onFechar">) {
  const [aberto, setAberto] = useState(false);
  return (
    <>
      <button type="button" onClick={() => setAberto(true)} className="text-xs font-semibold text-red-700 hover:underline dark:text-red-400">
        Dar como perdido
      </button>
      <PerdaDialog {...props} aberto={aberto} onFechar={() => setAberto(false)} />
    </>
  );
}
